import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Building2,
  QrCode,
  Copy,
  Check,
  AlertCircle,
  Clock,
  Users,
  Shield,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "../lib/utils/clipboard";

interface GeneratedToken {
  token: string;
  patientEmail: string;
  generatedAt: string;
  expiresAt: string;
  isUsed: boolean;
}

export function ClinicVerification() {
  const { profile } = useAuth();
  const [patientEmail, setPatientEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<GeneratedToken | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [recentTokens, setRecentTokens] = useState<GeneratedToken[]>([
    // Mock recent tokens for demo
    {
      token: "DEMO-1234",
      patientEmail: "patient@example.com",
      generatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      expiresAt: new Date(Date.now() + 22 * 60 * 60 * 1000).toISOString(),
      isUsed: true,
    },
    {
      token: "TEST-5678",
      patientEmail: "test@patient.com",
      generatedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      expiresAt: new Date(Date.now() + 19 * 60 * 60 * 1000).toISOString(),
      isUsed: false,
    },
  ]);

  // Check if user is clinic
  if (!profile || profile.role !== 'clinic') {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Access denied. This page is only accessible to clinic accounts.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const generateToken = async () => {
    if (!patientEmail.trim()) {
      toast.error("Please enter a patient email address");
      return;
    }

    if (!patientEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsGenerating(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate a random token
      const token = `${profile.display_name?.split(' ')[0].toUpperCase() || 'CLINIC'}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours

      const newToken: GeneratedToken = {
        token,
        patientEmail,
        generatedAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        isUsed: false,
      };

      setGeneratedToken(newToken);
      setRecentTokens(prev => [newToken, ...prev.slice(0, 9)]); // Keep last 10

      toast.success("Verification token generated successfully");
      console.log('✅ Generated QR token:', token);
    } catch (error) {
      console.error('Error generating token:', error);
      toast.error("Failed to generate token");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToken = async (token: string) => {
    try {
      await copyToClipboard(token);
      setCopiedToken(true);
      toast.success("Token copied to clipboard");
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
      toast.error("Failed to copy token. Please copy manually.");
      setCopiedToken(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const hoursRemaining = Math.floor((expiry.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (hoursRemaining < 0) return "Expired";
    if (hoursRemaining < 1) return "< 1 hour";
    return `${hoursRemaining} hours`;
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => window.location.hash = '#/clinic-dashboard'}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl">Patient Verification</h1>
              <p className="text-muted-foreground">{profile.display_name}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Generate Token Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary" />
                Generate Patient Verification Token
              </CardTitle>
              <CardDescription>
                Create a secure 24-hour token for patient verification. Patients scan the QR code or enter the token manually.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Input */}
              <div className="space-y-2">
                <Label htmlFor="patient-email">Patient Email Address</Label>
                <div className="flex gap-2">
                  <Input
                    id="patient-email"
                    type="email"
                    placeholder="patient@example.com"
                    value={patientEmail}
                    onChange={(e) => setPatientEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && generateToken()}
                  />
                  <Button onClick={generateToken} disabled={isGenerating} className="shrink-0">
                    {isGenerating ? "Generating..." : "Generate Token"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter the patient's email address to generate a verification token
                </p>
              </div>

              {/* Generated Token Display */}
              {generatedToken && (
                <div className="border-2 border-primary rounded-lg p-6 bg-primary/5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Verification Token</p>
                      <div className="flex items-center gap-3">
                        <code className="text-2xl font-mono font-bold tracking-wider">
                          {generatedToken.token}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToken(generatedToken.token)}
                          className="shrink-0"
                        >
                          {copiedToken ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-primary/20">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Patient Email</p>
                      <p className="font-medium">{generatedToken.patientEmail}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Expires</p>
                      <p className="font-medium">{formatDate(generatedToken.expiresAt)}</p>
                    </div>
                  </div>

                  <Alert className="mt-4">
                    <Shield className="w-4 h-4" />
                    <AlertDescription className="text-sm">
                      Share this token with <strong>{generatedToken.patientEmail}</strong> via secure email or in person. The token expires in 24 hours.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Verification Stats
              </CardTitle>
              <CardDescription>Today's verification activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tokens Generated</span>
                  <span className="text-2xl font-bold">{recentTokens.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tokens Used</span>
                  <span className="text-2xl font-bold text-primary">
                    {recentTokens.filter(t => t.isUsed).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Active Tokens</span>
                  <span className="text-2xl font-bold text-amber-600">
                    {recentTokens.filter(t => !t.isUsed && new Date(t.expiresAt) > new Date()).length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Tokens */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Recent Tokens
              </CardTitle>
              <CardDescription>
                Verification tokens generated in the last 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentTokens.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No tokens generated yet
                  </p>
                ) : (
                  recentTokens.map((token, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <code className="font-mono font-bold px-3 py-1 bg-muted rounded">
                          {token.token}
                        </code>
                        <div className="flex-1">
                          <p className="font-medium">{token.patientEmail}</p>
                          <p className="text-sm text-muted-foreground">
                            Generated {formatDate(token.generatedAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {getTimeRemaining(token.expiresAt)}
                          </p>
                          <p className="text-xs text-muted-foreground">remaining</p>
                        </div>
                        <Badge variant={token.isUsed ? "default" : "secondary"}>
                          {token.isUsed ? "Used" : "Active"}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Help Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>How to Use Verification Tokens</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">1</span>
                </div>
                <h3 className="font-semibold">Generate Token</h3>
                <p className="text-sm text-muted-foreground">
                  Enter the patient's email and click "Generate Token"
                </p>
              </div>
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">2</span>
                </div>
                <h3 className="font-semibold">Share Securely</h3>
                <p className="text-sm text-muted-foreground">
                  Send the token to the patient via secure email or display QR code in person
                </p>
              </div>
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-bold text-primary">3</span>
                </div>
                <h3 className="font-semibold">Patient Verifies</h3>
                <p className="text-sm text-muted-foreground">
                  Patient scans QR code or enters token on CannaBias to get verified
                </p>
              </div>
            </div>

            <Alert>
              <Shield className="w-4 h-4" />
              <AlertDescription className="text-sm">
                <strong>Privacy & Security:</strong> Tokens contain no medical or personal data. They only verify that a patient is registered with your clinic. Tokens expire after 24 hours and can only be used once.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
